import { Category } from '../tenant/category';
import { CodeType, SorCodeScope } from './code.type';
import { SorEvidenceSettings } from './sor.evidence';
import { TaskGate } from '../tasks/task.evidence';
import type { TaskEvidenceOutcome } from '../tasks/task.evidence.outcome';
import { SorItemFields } from './sor.item';
import { Entity } from '../entity';
import { SupervisorCheck } from '../compliance/supervisor.check';
import { UoM, CodeUoM } from './uom';
import { TaxRate } from '../taxrate/tax.rate';
import { PriceListCategory } from '../price.list.category';
import { CustomFieldValue } from '../fields/custom.field.value';
import { InvoiceStatus } from '../invoicing/invoicing.enum';
import { EntityStatusStamp } from '../entity.status/entity.status.stamp';
import { Source } from '../../enums/enum.source';
import { ItemCreator } from './item.creator';
import { ItemLinkEntity } from '../entity';
import { EntityType } from './entity.type';
import { PagingOptions } from './paging.options';
import { SystemLabels } from './label';
import type { FinanceMarks, ItemFinanceLock } from '../job/finance.marks';
import { WorkOrderStatus } from '../job/workorder.status';
import { ContactEntity } from '../contact/contact.options';
export enum CostSource {
    StandardCost = 'Standard Cost',
    TradePrice = 'Trade',
    // Manual = 'Manual'
}

export enum CodeStatus {
    Normal = 'Normal',
    Variation = 'Variation',
    Cancelled = 'Cancelled',

    /** @deprecated  Use 'Requested' instead */
    Pending = 'Pending',

    Requested = 'Requested',
    PendingReview = 'Pending Review',
    Declined = 'Declined',

    Deleted = 'Deleted',
}

export enum CodeInvoiceStatus {
    Billable = 'Billable',
    /** @deprecated Stored lines keep this value and the backend still honours it. */
    NoCharge = 'No Charge',
    NotBillable = 'Not Billable'
}

export interface GroupCodeCategory {
    categoryName: string;
    subcategories: Category[];
}

export interface CodeCategory {
    _id?: string;
    name: string;
    categoryId: string;
    categoryName: string;
    subCategoryId: string;
    subCategoryName: string;
}


export const recentCodeCategory = {
    _id: 'recent',
    name: 'System | Recently Added',
    categoryName: '<System>',
    categoryId: 'systemRecent',
    subCategoryName: 'Recently Added',
    subCategoryId: 'recent',
} as CodeCategory;

export const zeroPriceCodeCategory = {
    _id: 'zero',
    name: 'System | Items with Zero Price',
    categoryName: '<System>',
    categoryId: 'systemZero',
    subCategoryName: 'Items with Zero Price',
    subCategoryId: 'zero',
} as CodeCategory;

export const searchCodeCategory = {
    _id: 'search',
    name: 'System | Search',
    categoryName: '<System>',
    categoryId: 'systemSearch',
    subCategoryName: 'Search',
    subCategoryId: 'search',
} as CodeCategory;

export interface CodePriceBook {
    _id: string;
    name: string;
    category?: PriceListCategory;
    price: number;
    codeId?: string;

    updatedAt?: Date;
    // updatedBy?: Entity;
}


export interface JobCodeExport {
    type?: CodeType;
    name: string;
    quantity: number;
    description: string;
    location: string;
    note: string;
    status: CodeStatus;
    codeUoM: CodeUoM;
    buyPrice: number;
    sellPrice: number;
    codeInvoicingStatus: CodeInvoiceStatus;
    suppliedBySubcontractor: boolean;
    payable?: boolean;
    stageNumber?: string;
    stageName?: string;
}

export interface Code extends Entity {
    type: CodeType;
    tenantName?: string;
    parentType?: CodeType;
    category: CodeCategory;
    description: string;
    longDescription?: string;
    jobNumber?: string;
    childCodes?: ChildCode[];
    uoms?: CodeUoM[];
    location?: string;
    quantity?: number;
    customFields?: any;
    suppliedBySubcontractor?: boolean;
    // priceBooks?: CodePriceBook[];

    autoCreateTask?: boolean;
    isClaimCode?: boolean;
}
export interface CatalogueCode extends Code {
    //jobNumber?: string;
    childCodes?: ChildCode[];
    //locked?: boolean;
    labels?: number;
    uoms?: CodeUoM[]; // Additional uom list
    baseUoM?: UoM;
    manufacturer?: string;
    retailPrice?: number;
    tradePrice?: number;
    standardCost?: number;
    economicOrderQty?: number;
    safetyStockQty?: number;
    leadTimeDays?: number;
    longDescription?: string;
    upc?: string;

    itemSold?: boolean;
    saleGlCode?: string;
    saleTaxRate?: TaxRate;
    codeInvoicingStatus?: CodeInvoiceStatus;

    itemPurchased?: boolean;
    purchaseGlCode?: string;
    purchaseTaxRate?: TaxRate;

    /**
     * The Payable answer new lines inherit. Absent reads as payable, exactly as on the line, so the
     * catalogue ships no default and an item nobody has answered keeps behaving as it always did.
     */
    payable?: boolean;

    /**
     * Job tags (LabelType.Job) this item brings onto the job and the stage holding it, e.g. High Risk.
     * Read from the catalogue when a stage's tags are worked out, never copied onto the line, so
     * ToEmbeddedCode leaves it behind.
     */
    jobLabels?: Entity[];

    canOverrideDesc?: boolean;

    /**
     * SOR only, and sparse on purpose: profile-scoped (`{ pending, authorised }`), with each bucket
     * left out inheriting the tenant default for that profile, which is how an administrator clears
     * an override. The dense form is resolved onto the item.
     */
    sorEvidenceOverride?: SorEvidenceSettings;
    /** SOR only, and stored only when it differs from the tenant default so clearing it means inheriting again. */
    taskGateOverride?: TaskGate;

    trackingCategory1?: string;
    trackingCategory2?: string;

    /** MD5 of the last embedded text. Null = never synced or just evicted. */
    _vectorHash?: string;
    /** Drift gate. updatedAt > _lastVectorSyncedAt → needs re-sync. */
    _lastVectorSyncedAt?: Date;
    /** Last failure message. No code reads this; surfaced for ops/UI only. */
    _vectorSyncError?: string;
}

/**
 * A catalogue code as returned by the expand-for-document query. `parentId` is the only link from an
 * expanded child back to the composite that was picked (each child carries its own `_id`); it is
 * response-only and never stored, so it stays off `Code` where every `Required<Code>` would demand it.
 */
export type ExpandedCatalogueCode = CatalogueCode & { readonly parentId?: string };

export interface CodeFinance {
    _id: string;
    name: string;

    itemSold?: boolean;
    saleGlCode?: string;
    saleTaxRate?: TaxRate;

    itemPurchased?: boolean;
    purchaseGlCode?: string;
    purchaseTaxRate?: TaxRate;
}

/**
 * What a copy of a catalogue item, template line or visit line becomes on a job: No Charge is written
 * out as Not Billable at zero sell. The money is identical either way. The difference is that the
 * result is one of the two answers the editors still offer, so the line can be answered, filtered and
 * reported on.
 *
 * Copies only. A line already on a job keeps whatever it holds, including when it moves between
 * stages, because this epic changes no stored data. It lives here rather than beside the finance mark
 * helpers because `code.ts` reaches `entities/job` through `import type` alone, and a value import
 * would close that circle.
 */
export function normaliseRetiredInvoicingStatus<T extends RetiredInvoicingStatusItem>(item: T): T {

    if (item?.codeInvoicingStatus !== CodeInvoiceStatus.NoCharge) {
        return item;
    }

    return { ...item, codeInvoicingStatus: CodeInvoiceStatus.NotBillable, sellPrice: 0, customSellPrice: false };
}

type RetiredInvoicingStatusItem = { codeInvoicingStatus?: CodeInvoiceStatus; sellPrice?: number; customSellPrice?: boolean };

export function ToEmbeddedCode(c: CatalogueCode): EmbeddedCodeLike {

    const item = {
        type: c.type,
        parentType: c.parentType,
        category: c.category,
        description: c.description,
        longDescription: c.longDescription,
        // priceBooks: c.priceBooks,
        quantity: c.quantity ?? 1,
        location: c.location ?? '',
        customFields: c.customFields,
        name: c?.name?.trim(),
        _id: c._id,
        suppliedBySubcontractor: c.suppliedBySubcontractor,
        // Carried, never defaulted: absent means payable on both grains, and pricing on create
        // zeroes buy for an explicit false, so a Not Payable catalogue item lands as a Not
        // Payable line at zero on every create path.
        payable: c.payable,
        codeUoM: c?.['codeUoM'] ?? { uom: c.baseUoM, conversionRate: 1 },

        manufacturer: c.manufacturer,

        itemSold: c.itemSold,
        saleGlCode: c.saleGlCode,
        saleTaxRate: c.saleTaxRate,

        itemPurchased: c.itemPurchased,
        purchaseGlCode: c.purchaseGlCode,
        purchaseTaxRate: c.purchaseTaxRate,

        autoCreateTask: c.autoCreateTask,
        canOverrideDesc: c.canOverrideDesc,
        data: c.data
    } as EmbeddedCode;

    // Both arms of the old itemSold ternary read the same, so the branch decided nothing.
    item.codeInvoicingStatus = c?.codeInvoicingStatus ?? CodeInvoiceStatus.Billable;

    return normaliseRetiredInvoicingStatus(item);
}

export type AdjustmentType = 'Value' | 'Percent';

/**
 * The status of the invoice and bill a line is linked to, as the legacy Items tab read it. Stamped on a
 * read (`GetItem`, the legacy codes repository) and by the update handlers before the locked-field
 * guard runs; the stored line carries only the refs, and the lock answers to the document's status.
 */
export interface ItemFinanceApproval {
    invoiceApproved?: boolean;
    invoiceStatus?: InvoiceStatus;
    billApproved?: boolean;
    billStatus?: InvoiceStatus;
    purchaseOrderApproved?: boolean;
}

export interface EmbeddedCode extends Code, SorItemFields, ItemFinanceApproval {
    source?: Source;
    buyPrice?: number;
    sellPrice?: number;
    customBuyPrice?: boolean;
    customSellPrice?: boolean;
    completed?: boolean;
    answered?: boolean;
    supervisorCheck?: SupervisorCheck;
    status?: CodeStatus;

    /**
     * Entity-status stamp used by authorised and pending lines. A legacy Item row may still carry only
     * `status` until its next status-aware write. Every new authorised Item stores both this canonical
     * status and a mirrored CodeStatus for classic consumers. Declared on EmbeddedCode because the `workOrders.codes`
     * sub-schema has to declare it to persist it: strict mode drops an undeclared path silently.
     *
     * A stamp rather than the status itself: the line must read the same after a tenant renames or retires
     * the status it was set to. Field names and `_id`s match what the sub-status stored, so existing
     * readers of `_id`, `name`, `systemName`, `statusType` and `legacyStatus` are unaffected.
     */
    itemStatus?: EntityStatusStamp;
    integrationId?: string;
    location?: string;
    note?: string;
    codeUoM?: CodeUoM;
    baseQuantity?: number;
    codeInvoicingStatus?: CodeInvoiceStatus;
    suppliedBySubcontractor?: boolean;
    /** The catalogue item's own values, snapshotted by ToEmbeddedCode. Every line of the same code carries the same set. */
    customFields?: any;
    /**
     * This line's own custom field values, configured against CustomFieldParent.EmbeddedItem.
     * Kept apart from `customFields` because one CustomField definition can be mapped to both
     * parents, and a shared bag would let the catalogue snapshot and the line's own answer
     * collide on the same fieldId.
     */
    lineCustomFields?: any;
    upc?: string;
    uom?: string;
    bill?: ItemLinkEntity;
    invoice?: ItemLinkEntity;
    purchaseOrder?: ItemLinkEntity;
    stage?: ItemLinkEntity;
    quoteSection?: ItemLinkEntity;

    itemSold?: boolean;
    saleGlCode?: string;
    saleTaxRate?: TaxRate;

    itemPurchased?: boolean;
    purchaseGlCode?: string;
    purchaseTaxRate?: TaxRate;
    defectCount?: number;
    timesheetIds?: string[];

    manufacturer?: string;

    /** Id Code from Setting > Items  */
    catalogueCodeId?: string;
    sortOrder?: number;

    canOverrideDesc?: boolean;

    adjustmentType?: AdjustmentType;
    adjustment?: number;
    adjustedSell?: number;

    totalBuy?: number;
    totalSellEx?: number;
    totalAdjustment?: number;
    totalTax?: number;
    totalSellInc?: number;
    itemTaskCompleted?: boolean;
    /** Server owned: the main task's evidence outcome mirrored for the SOR board; the task stays the source. */
    evidenceOutcome?: TaskEvidenceOutcome;

    auditDetails?: CodeAuditDetails;

    /** Who raised the line, for the Draft edit window and the office-only move (E1-05). */
    raisedBy?: ItemCreator;
    claimLinked?: boolean;
    claimImport?: boolean;
    /** The client lock: set by integrations only, refuses every user write but the instruction (E1-05). */
    isReadonly?: boolean;

    // Residual line capability. No status projects these any more — what a status permits is `editableBy`
    // — so only an integration still writes them. E1-14 removes the field and its data.
    allowEdit?: boolean;
    allowDelete?: boolean;

    // row number in import csv file
    rowNumber?: number;
}

export type EmbeddedCodeAction = 'edit' | 'delete';

/**
 * What the line itself permits, before the status has its say.
 *
 * The client lock is answered first: an integration owns the line, so no line-level permission it was
 * once given may talk over it. What the *status* permits is `editableBy`, resolved by the caller.
 */
export function resolveCodeCapability(code: Pick<EmbeddedCode, 'allowEdit' | 'allowDelete' | 'isReadonly'>, action: EmbeddedCodeAction): boolean {

    if (code?.isReadonly === true) return false;

    // Null reads as absent — the retired status projection wrote it to clear a prior value.
    return (action === 'edit' ? code?.allowEdit : code?.allowDelete) ?? true;
}

/** A pending line has no CodeStatus at all: its status is the inherited `itemStatus` (ItemPending). */
export interface PendingCode extends Omit<EmbeddedCode, 'status'> {
}

export interface ApprovedCode extends EmbeddedCode {
}

/**
 * Any code-shaped line, whichever array it came from.
 *
 * The intersection currently collapses to `EmbeddedCode`: `itemStatus` moved onto it, which was
 * `PendingCode`'s only own member. The three names are kept because they say where a line lives, but
 * nothing at the type level now separates an authorised line from a pending one. Their status/storage
 * invariants are enforced by the Stage Item and Stage Pending Item domain guards.
 */
export type EmbeddedCodeLike = EmbeddedCode & PendingCode & ApprovedCode;

export interface ChildCode extends Entity {
    description?: string;
    category?: CodeCategory;
    quantity?: number;
    codeUoM?: CodeUoM;
    codeInvoicingStatus?: CodeInvoiceStatus;
    payable?: boolean;
    suppliedBySubcontractor?: boolean;
}

export interface CodeImportSystemResponse {
    name: string;
    message: string[];
    index: number;
    expanded?: boolean;
}

export interface PatchJobCodeRequest {
    jobId?: string;
    workOrderId: string;
    codeId: string;
    defectCount?: number;
    createActivity?: boolean;
}

export interface PatchItemsRequest {
    jobId?: string;
    workOrderItemPatchData: PatchWorkOrderItemsRequest[];
}

export interface BulkActionItemsRequest extends PatchItemsRequest {
    action: WorkOrderCodesBulkAction;
}

export interface BulkActionItemsResponse {
    action: WorkOrderCodesBulkAction;
    responseData: any
}

export interface PatchWorkOrderItemsRequest {
    workOrderId: string;
    codes: EmbeddedCodeLike[];
}

export interface WorkOrderCodeEntity extends Entity {
    workOrderNumber: string;
    /** This stage's own finance marks. Resolved against the job's by `resolveItemFinanceLock`. */
    finance?: FinanceMarks;
    technician: Entity;
    resources: ContactEntity[];
    status: WorkOrderStatus;
    jobSystemLabels: SystemLabels[];
    workOrderName?: string;
    workCategory?: Entity;
    sortOrder?: string;
    /** Materialised money figures (WBS-10165) — surfaced for the QA validation rows in the items footer. */
    totalSell?: number;
    totalBuy?: number;
    claimAmount?: number;
}
export interface EmbeddedCodeGroup extends EmbeddedCodeLike {
    job: Entity;
    workOrder: WorkOrderCodeEntity;
    location: string;
    entityType?: EntityType;
    selected?: boolean;

    canEdit?: boolean;
    canDelete?: boolean;
    canEditFields?: Partial<Record<keyof EmbeddedCode, boolean>>;
    /**
     * The marks in force on this line, its stage's resolved against the job's. Carried beside
     * `canEditFields` because a locked field has to say WHY, and the resolve needs the job and every
     * stage on the tab — which the row itself does not hold.
     */
    financeLock?: ItemFinanceLock;

    // Separate from canEdit — media (attachments) are metadata about the line, not the line's values.
    // Tracks user manage permission + WO state so media management works on readonly lines.
    canManageMedia?: boolean;
}


export interface EmbeddedCodeMetadataTab {
    job: Entity;
    workOrder: WorkOrderCodeEntity;
    dateFormat: string;
    timezone: string;
    type: EntityType;
    workOrders?: WorkOrderCodeEntity[];
    sellPricebook?: Entity;
    workOrderUpdatedAt?: Date;
    currentUser?: Entity;
    woReadOnlyAfterFinalised: boolean;
    // Locks the items toolbar (import / add) when the job is closed or the stage is closed/cancelled.
    readonly?: boolean;
    jobSystemLabels?: Entity[];
    /**
     * The JOB's own finance marks. A line's effective lock is this resolved against its stage's, so
     * the per-stage half rides on `workOrder` / `workOrders` — a job-scoped tab lists lines from
     * several stages, and only some of them may be marked.
     */
    finance?: FinanceMarks;
}

export interface CodeAndCustomFieldResponse {
    customFields: CustomFieldValue[];
    codeSummary: Partial<CatalogueCode>;
}

export interface KitItemEntity extends Entity {
    category: CodeCategory;
}

export interface CodeCategoryEntity extends Entity {
    description: string;
    type: CodeType;
    baseUoM?: UoM;
    uoms?: CodeUoM[];
}

export interface GetCodeCategoryEntitiesRequest {
    category?: string;
    excludePricebook?: string;
    isCodeItem?: boolean;
    isSold?: boolean;
    isPurchased?: boolean;
    excludeTypes: CodeType[];
    /** Absent means Include. See SorCodeScope. */
    sorScope?: SorCodeScope;
    paging?: PagingOptions;

}

export interface CodeAuditDetails {
    completedBy?: ContactEntity;
    completedAt?: Date;
    actualQuantity?: number;
    comply?: CodeAuditComply;
    complyNote?: string;
}

export enum CodeAuditComply {
    Pass = 'Pass',
    Fail = 'Fail',
    NA = 'NA'
}

export enum EmbeddedCodeGroupBy {
    WorkOrders = 'Stages',
    Location = 'Location',
    Status = 'Status',
    None = 'None'
}

export enum EmbeddedCodeFilterDefinition {
    AllActive = 'All Active',
    AllInactive = 'All Inactive',
    AllPending = 'All Pending',
    Normal = 'Normal',
    Variation = 'Variation',
    Cancelled = 'Cancelled',
    AllPendingActive = 'All Pending Active',
    AllPendingInactive = 'All Pending Inactive',
}

export enum EmbeddedCodeStateFilterDefinition {
    All = "All",
    Complete = "Complete",
    Incomplete = "Incomplete",
    SuppliedByContactor = "Supplied By Contractor",
    NotSuppliedByContractor = "Not Supplied By Contractor",
    CanBePurchased = "Items to Purchase",
    ItemsOrdered = "Items Ordered",
}

export enum WorkOrderCodesBulkAction {
    Approved = 'Approved',
    UpdateStatus = 'Update Status',
    Delete = 'Delete',
    Cancel = 'Cancel',
}

export enum EmbeddedItemCommandId {
    GetItems = 'GetItems',
    GetItem = 'GetItem',
    AddItems = 'AddItems',
    SaveItem = 'SaveItem',
    DeleteItems = 'DeleteItems',
    MoveItems = 'MoveItems',
    ImportItems = 'ImportItems',
    ApproveItems = 'ApproveItems',
    UpdateItemStatus = 'UpdateItemStatus',
    BulkAction = 'BulkAction',
    GetItemInfos = 'GetItemInfos',
    GetDefects = 'GetDefects',
    SaveDefect = 'SaveDefect',
    CreatePurchaseOrder = 'CreatePurchaseOrder',
}

/**
 * Items board mutations that have no semantic method on the entity-data surface, dispatched as
 * `POST /api/entity/JobItem/data/command/<id>`. Delete is absent on purpose: it maps onto the
 * strategy's own `delete`, so it keeps the framework's item-list invalidation.
 *
 * Distinct from EmbeddedItemCommandId, which belongs to embedded item boards and has
 * no server handler for any of its ids.
 */
export enum JobItemCommandId {
    AddItems = 'AddItems',
    UpdateItem = 'UpdateItem',
    GetItem = 'GetItem',
    DeleteItem = 'DeleteItem',
    ApproveItem = 'ApproveItem',
    RequestItem = 'RequestItem',
    RequestItems = 'RequestItems',
    CancelItem = 'CancelItem',
    CreatePurchaseOrder = 'CreatePurchaseOrder',
    /** Field app SOR batch: one comment posted onto each selected SOR's thread (WBS-12801). */
    CommentItems = 'CommentItems',
    /** Field app SOR batch: each selected SOR's main task moved to the Completed status (WBS-12802). */
    CompleteItems = 'CompleteItems',
    /** Field app SOR batch: each selected SOR's main task moved to one chosen closing status and reason (WBS-12803). */
    ResolveItems = 'ResolveItems',
    /** Field app SOR batch: one evidence exception (Can't capture) recorded on each selected SOR's main task (WBS-11470). */
    RecordEvidenceExceptionItems = 'RecordEvidenceExceptionItems',
    /** Field app SOR batch: the same job photos linked to one evidence collection of each selected SOR's main task (WBS-12800). */
    AttachItemEvidence = 'AttachItemEvidence',
}

/**
 * The ⌘ Actions status moves on the Items and SORs boards: one per line kind and target type, so each
 * dialog offers one set and each run skips the lines of the other kind (WBS-12289).
 */
export enum JobItemStatusBulkActionId {
    PendingChangeStatus = 'items.pending.status.change',
    PendingCancel = 'items.pending.cancel',
    AuthorisedChangeStatus = 'items.authorised.status.change',
    AuthorisedCancel = 'items.authorised.cancel',
}

/** Every status move needs the section's Edit, so the board withholds all four together. */
export const ITEM_STATUS_BULK_ACTION_IDS: string[] = Object.values(JobItemStatusBulkActionId);

/** The SORs tab's own ⌘ Actions (WBS-12433): the Items (new) tab never lists them. */
export enum JobItemSorBulkActionId {
    Request = 'items.pending.request',
    Decline = 'items.pending.decline',
    DeleteDraft = 'items.pending.draft.delete',
    Complete = 'items.authorised.complete',
    Resolve = 'items.authorised.resolve',
    Reopen = 'items.authorised.reopen',
}

export const ITEM_SOR_BULK_ACTION_IDS: string[] = Object.values(JobItemSorBulkActionId);

export interface EmbeddedCodeViewOptions {
    groupBy: EmbeddedCodeGroupBy;
    statusFilters: EmbeddedCodeFilterDefinition[];
    stateFilter: EmbeddedCodeStateFilterDefinition;
    search?: string,

    isTextSearch?: boolean;
}

export enum EmbeddedCodeViewVariant {
    JobCodes = 'JobCodes',
}
